// Optimized InwardItemsEntry Component with debounced inputs and memoized totals

import {
    faPlus,
    faRemove
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { Button, Input } from "@nextui-org/react";
import React, {
    useCallback,
    useEffect,
    useRef,
    useState,
    useMemo,
    startTransition
} from "react";
import { isMobile } from "react-device-detect";
import { calculateTotal } from "../../Utility/utility";
import { useFetchWithBody, usePost } from "../../api/reactQueryCustomHooks";
import {
    apiRoute,
    ENUM_MOD_NAME,
    ENUM_MOD_PARAMETER,
    getModVisiblity
} from "../../constants/enum";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import debounce from "lodash.debounce";

const InwardItemsEntry = ({ tradeOf, settingMode, inwardItemData, handleSelectedInwardItem }) => {
    const [selInwardItem, setSelInwardItem] = useState(inwardItemData);
    const [selRowIndex, setSelRowIndex] = useState(0);
    const [deletedItemRows, setDeletedItemRows] = useState([]);
    const { compCode } = useSelector((state) => state.company);
    const [modSettingParams, setModSettingParams] = useState();
    const [selTradeOf, setSelTradeOf] = useState(tradeOf);
    const [cachedTotals, setCachedTotals] = useState({});

    const RowsField = {
        id: "id",
        takaId: "TakaId",
        inwCompCode: "InwCompCode",
        cartonNo: "CartonNo",
        pcs: "Pcs",
        grossWeight: "GWeight",
        netWeight: "Weight",
        tareWeight: "TWeight",
        itemCode: "Item_Code",
        itemName: "Item_Desc",
        meter: "Meter"
    };

    const initialRow = {
        [RowsField.cartonNo]: "",
        [RowsField.takaId]: 0,
        [RowsField.pcs]: 0,
        [RowsField.grossWeight]: 0,
        [RowsField.tareWeight]: 0,
        [RowsField.netWeight]: 0,
        [RowsField.meter]: 0,
        [RowsField.inwCompCode]: 0
    };

    const { isError: isErrorModSet, data: modSetting = [], mutate: filterModSetting } = usePost({
        url: apiRoute.modeSetting.getModSetting,
        queryKey: "fetchModSettingInwardItem",
    });

    const { data: takaListcheckDup = [] } = useFetchWithBody({
        url: apiRoute.transaction.SaleEntry.fetchTakaListTocheck,
        queryKey: "fetchTakalIst",
        inputData: {
            compcode: compCode.join(",")
        }
    });

    useEffect(() => {
        setSelInwardItem(inwardItemData);
    }, [inwardItemData]);

    useEffect(() => {
        const filtered = selInwardItem.filter(ele => ele.CartonNo !== "");
        handleSelectedInwardItem({ item: filtered, deletedItem: deletedItemRows });
    }, [selInwardItem]);

    useEffect(() => {
        if (settingMode) {
            filterModSetting({ data: { mod_name: settingMode } });
        }
    }, []);

    useEffect(() => {

        if (modSetting && modSetting?.length > 0) {

            setModSettingParams({
                Visibility: {
                    [RowsField.cartonNo]: getModVisiblity(ENUM_MOD_PARAMETER.inwardItemEntry.CartonNo, modSetting) ?? false,
                    [RowsField.pcs]: getModVisiblity(ENUM_MOD_PARAMETER.inwardItemEntry.Pcs, modSetting) ?? false,
                    [RowsField.meter]: getModVisiblity(ENUM_MOD_PARAMETER.inwardItemEntry.Meter, modSetting) ?? false,
                    [RowsField.netWeight]: getModVisiblity(ENUM_MOD_PARAMETER.inwardItemEntry.NWeight, modSetting) ?? false,
                    [RowsField.grossWeight]: getModVisiblity(ENUM_MOD_PARAMETER.inwardItemEntry.GWeight, modSetting) ?? false,
                    [RowsField.tareWeight]: getModVisiblity(ENUM_MOD_PARAMETER.inwardItemEntry.TWeight, modSetting) ?? false,
                },
            })
        }

    }, [modSetting])

    useEffect(() => {
        const timeout = setTimeout(() => {
            setCachedTotals({
                total_GWeight: calculateTotal({ data: selInwardItem, columnName: RowsField.grossWeight }),
                total_TWeight: calculateTotal({ data: selInwardItem, columnName: RowsField.tareWeight }),
                total_NWeight: calculateTotal({ data: selInwardItem, columnName: RowsField.netWeight }),
                total_Meter: calculateTotal({ data: selInwardItem, columnName: RowsField.meter }),
                total_Pcs: calculateTotal({ data: selInwardItem, columnName: RowsField.pcs }),
                total_Taka: selInwardItem.filter((ele) => ele[RowsField.cartonNo] !== "").length,
            });
        }, 200);
        return () => clearTimeout(timeout);
    }, [selInwardItem]);

    const funGenRows = () => {
        const newRow = { Id: selInwardItem.length + 1, ...initialRow };
        setSelInwardItem((prev) => [...prev, newRow]);
    };

    const funGridSetting = (id, index) => {
        let columnId = RowsField.tareWeight;
        if (modSettingParams && Object.keys(modSettingParams).length > 0) {
            if (modSettingParams.Visibility && Object.keys(modSettingParams.Visibility).length > 0) {
                if (modSettingParams.Visibility[RowsField.pcs] || modSettingParams.Visibility[RowsField.meter])
                    columnId = RowsField.cartonNo;
                if (modSettingParams.Visibility[RowsField.grossWeight]) {
                    if (modSettingParams.Visibility[RowsField.pcs])
                        columnId = RowsField.pcs;
                    else
                        columnId = RowsField.meter;
                }
                if (modSettingParams.Visibility[RowsField.tareWeight]) {
                    if (modSettingParams.Visibility[RowsField.grossWeight])
                        columnId = RowsField.grossWeight;
                    else
                        columnId = RowsField.pcs;
                }
                if (modSettingParams.Visibility[RowsField.netWeight]) {
                    if (modSettingParams.Visibility[RowsField.tareWeight])
                        columnId = RowsField.tareWeight;
                    else
                        columnId = RowsField.meter;
                }
            }
            if (id === columnId) {
                if (index === selInwardItem.length - 1) {
                    funGenRows();
                }
            }
        }
    };

    const funSetChallanEntry = (index, name) => {
        const newRow = [...selInwardItem];
        let t_takano = newRow[index][RowsField.cartonNo]?.trim();
        let isduplicate = 0;

        if (t_takano !== "") {
            if (name === RowsField.cartonNo) {
                const isDupInServer = takaListcheckDup.some(item => item["Taka_No"].toLowerCase() === t_takano.toLowerCase());
                const isDupInCurrent = selInwardItem.some((item, idx) =>
                    idx !== index && item[RowsField.cartonNo]?.toLowerCase() === t_takano.toLowerCase()
                );

                if ((isDupInServer || isDupInCurrent) && settingMode === ENUM_MOD_NAME.GrayPurchaseTakaEntry) {
                    newRow[index][RowsField.cartonNo] = "";
                    setSelInwardItem([...newRow]);
                    toast.error("Taka No " + t_takano + " is already added", {
                        position: "top-center",
                        onClose: () => { setSelInwardItem([...newRow]); }
                    },

                    );


                }
            }
        }
        funGridSetting(name, index);
    };


    const handleBlur = (rowId, e) => {
        const { id, value } = e.target;
        let index = selInwardItem.findIndex(ele => ele.Id === rowId);
        setSelRowIndex(index);
        debouncedInputChange(rowId, id, value);
        funSetChallanEntry(index, id);
    };

    const debouncedInputChange = useMemo(() =>
        debounce((rowId, id, value) => {
            setSelInwardItem((prevData) => {
                const index = prevData.findIndex(ele => ele.Id === rowId);
                if (index === -1) return prevData;

                const newData = [...prevData];

                let t_netWeight = newData[index][RowsField.netWeight];

                if (id === RowsField.grossWeight) {
                    t_netWeight = parseFloat(value) - parseFloat(newData[index][RowsField.tareWeight] ?? 0);
                } else if (id === RowsField.tareWeight) {
                    //console.log(id,value,index,newData[index],"hj")
                    t_netWeight = parseFloat(newData[index][RowsField.grossWeight] ?? 0) - parseFloat(value);
                } else if (id === RowsField.netWeight) {
                    t_netWeight = parseFloat(value);
                }

                newData[index] = {
                    ...newData[index],
                    [id]: value,
                    [RowsField.netWeight]: t_netWeight
                };

                return newData;
            });
        }, 200), [modSettingParams, selInwardItem]);


    const handleInputChange = (rowId, id, value) => {
        debouncedInputChange(rowId, id, value);
    };

    const renderInputCell = (row, input_id, input_type) => (
        <Input
            id={input_id}
            type={input_type}
            size="sm"
            // value={row[input_id] ?? ""} // ✅ Controlled input
            defaultValue={row[input_id]}
            //onKeyDown={(e) => handleKeyDown(row.Id, e)}
            onChange={(e) => handleInputChange(row.Id, input_id, e.target.value)}
            onBlur={(e) => handleBlur(row.Id, e)}
            className="max-w-xs mt-1"
            variant="bordered"
        />
    );
    const itemGridHeadClass = "bill-grid-style"
    const columns = [
        {
            field: RowsField.id,
            headerName: "No.",
            width: 30,
            headerClassName: itemGridHeadClass,
            renderCell: ({ row }) => (
                <div className="flex gap-3 justify-around">
                    {row.Id}
                    <strong>
                        <FontAwesomeIcon
                            color="red"
                            className="cursor-pointer text-md"
                            onClick={() => handleDeleteRow(row.Id)}
                            icon={faRemove}
                        />
                    </strong>
                </div>
            )
        },
        {
            field: RowsField.cartonNo,
            headerName: selTradeOf == 'C' ? "Taka No" : "Carton No",
            minWidth: 100,
            flex: 1,
            headerAlign: "center",
            headerClassName: itemGridHeadClass,
            renderCell: ({ row }) => renderInputCell(row, RowsField.cartonNo, "text", false, "", false, "", ""),
        },
        ...[RowsField.pcs, RowsField.meter, RowsField.grossWeight, RowsField.tareWeight].map(field => ({
            field,
            headerName: field.replace(/([A-Z])/g, ' $1').toUpperCase(),
            minWidth: 100,
            flex: 1,
            headerClassName: itemGridHeadClass,
            renderCell: ({ row }) => renderInputCell(row, field, field === RowsField.cartonNo ? "text" : "number")
        })),
        {
            field: RowsField.netWeight,
            headerName: "Weight",
            minWidth: 100,
            flex: 1,
            headerAlign: "center",
            headerClassName: itemGridHeadClass,
            renderCell: ({ row }) => (tradeOf == 'Y' ? (<Input
                id={RowsField.netWeight}
                type={"number"}
                size="sm"
                value={row[RowsField.netWeight] ?? ""} // ✅ Controlled input
                onChange={(e) => handleInputChange(row.Id, RowsField.netWeight, e.target.value)}
                onBlur={(e) => handleBlur(row.Id, e)}
                className="max-w-xs mt-1"
                variant="bordered"
            />) : (

                <Input
                    id={RowsField.netWeight}
                    type={"number"}
                    size="sm"
                    defaultValue={row[RowsField.netWeight]}
                    onChange={(e) => handleInputChange(row.Id, RowsField.netWeight, e.target.value)}
                    onBlur={(e) => handleBlur(row.Id, e)}
                    className="max-w-xs mt-1"
                    variant="bordered"
                />
            )
            )
        },
    ];

    const handleDeleteRow = (id) => {
        const updatedRows = selInwardItem.filter((row) => row.Id !== id);
        const deletedRow = selInwardItem.find((row) => row.Id === id);
        const rearrangedRows = updatedRows.map((row, index) => ({ ...row, Id: index + 1 }));
        setSelInwardItem(rearrangedRows);
        setDeletedItemRows((prev) => [...prev, deletedRow]);
    };

    const handleAddRow = () => {
        funGenRows();
    };

    return (
        <div>
            <Button size="sm" color="success" className="text-white mt-1" onPress={handleAddRow}>
                <FontAwesomeIcon icon={faPlus} className="mr-2" /> ADD ROW
            </Button>

            <Box sx={{ height: selInwardItem.length > 8 ? 370 : "auto", width: '100%' }}>
                <DataGrid
                    rows={selInwardItem}
                    columns={columns}
                    getRowId={(row) => row.Id}
                    hideFooterPagination={selInwardItem.length <= 100}
                    hideFooter={selInwardItem.length <= 100}
                    columnHeaderHeight={35}
                    rowHeight={45}
                    disableColumnSorting
                    disableColumnMenu

                    columnVisibilityModel={{
                        [RowsField.cartonNo]: modSetting && modSetting?.length > 0 ? modSettingParams?.Visibility[RowsField.cartonNo] : true,
                        [RowsField.pcs]: modSetting && modSetting?.length > 0 ? modSettingParams?.Visibility[RowsField.pcs] : true,
                        [RowsField.meter]: modSetting && modSetting?.length > 0 ? modSettingParams?.Visibility[RowsField.meter] : true,
                        [RowsField.netWeight]: modSetting && modSetting?.length > 0 ? modSettingParams?.Visibility[RowsField.netWeight] : true,
                        [RowsField.grossWeight]: modSetting && modSetting?.length > 0 ? modSettingParams?.Visibility[RowsField.grossWeight] : true,
                        [RowsField.tareWeight]: modSetting && modSetting?.length > 0 ? modSettingParams?.Visibility[RowsField.tareWeight] : true,
                    }}
                />
            </Box>

            <div className="m-5 border-0 text-sm">
                <div className="flex justify-center gap-2">
                    <div className="flex flex-wrap bg-green-100 p-2 gap-2 min-w-32">
                        <div className="text-xs text-green-700">{tradeOf == 'Y' ? "CARTON" : "TAKA"}:</div>
                        <div className="text-sm text-slate-700">{cachedTotals.total_Taka}</div>
                    </div>
                    <div className="flex flex-wrap bg-green-100 p-2 gap-2 min-w-32">
                        <div className="text-xs text-green-700">PCS :</div>
                        <div className="text-sm text-slate-700">{cachedTotals.total_Pcs}</div>
                    </div>
                    <div className="flex flex-wrap bg-green-100 p-2 gap-2 min-w-32">
                        <div className="text-xs text-green-700">GWEIGHT :</div>
                        <div className="text-sm text-slate-700">{cachedTotals.total_GWeight}</div>
                    </div>
                    <div className="flex flex-wrap bg-red-100 p-2 gap-2 min-w-32">
                        <div className="text-xs text-red-700">METER :</div>
                        <div className="text-sm text-slate-700">{cachedTotals.total_Meter}</div>
                    </div>
                    <div className="flex flex-wrap bg-red-100 p-2 gap-2 min-w-32">
                        <div className="text-xs text-red-700">NET WEIGHT :</div>
                        <div className="text-sm text-slate-700">{cachedTotals.total_NWeight}</div>
                    </div>
                    <div className="flex flex-wrap bg-red-100 p-2 gap-2 min-w-32">
                        <div className="text-xs text-red-700">TWEIGHT :</div>
                        <div className="text-sm text-slate-700">{cachedTotals.total_TWeight}</div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default InwardItemsEntry;
